<template>
  <div id="areas" data-testid="destinations-section">
    <!-- セクションヘッダー: 左寄せ gold タイトル + 矢印 + gold 水平線 (サブタイトルなし)
         タイトル+矢印は View all destinations ボタンと同じ遷移 (検索モーダル Destinations タブ) -->
    <div class="mx-auto mb-4 flex items-center gap-x-2 px-4 md:w-11/12 md:max-w-[1216px] md:px-0">
      <h2 class="font-Spectral text-xl font-medium leading-7 tracking-[0.5px] text-gold4 md:text-[30px] md:leading-[40px]">
        <button class="flex items-center gap-x-2 hover:opacity-80" data-testid="destinations-title-btn" type="button" @click="onClickViewAll">
          Featured Destinations
          <img alt="" aria-hidden="true" class="h-6 w-6 shrink-0" :src="arrowRightGold500" />
        </button>
      </h2>
      <span aria-hidden="true" class="h-[0.5px] flex-grow bg-gold4" />
    </div>

    <!--
      Desktop (md+): 上段=2大カード(16:9) + 下段=5小カード(4:5) のZ字配置。画像はビューポート横幅いっぱい
        親 section の md:px-4 を打ち消すため md:-mx-4 を当ててブリードさせる
      Mobile      : 7枚すべてを4:5の小カードで横スクロール (Figma 仕様)
    -->
    <div class="hidden md:-mx-4 md:block md:w-screen">
      <div class="grid grid-cols-2">
        <DestinationCard
          v-for="(destination, index) in topLargeDestinations"
          :key="destination.id"
          variant="large"
          :destination="destination"
          :loading="index === 0 ? 'eager' : 'lazy'"
          :on-click="onDestinationClick"
        />
      </div>
      <div class="grid grid-cols-5">
        <DestinationCard v-for="destination in bottomSmallDestinations" :key="destination.id" variant="small" :destination="destination" :on-click="onDestinationClick" :show-view-more="true" />
      </div>
    </div>

    <div class="md:hidden">
      <div class="slide-scrollbar flex w-full snap-x snap-mandatory scroll-px-4 gap-x-2 overflow-x-auto px-4 pb-1" data-testid="destinations-mobile-carousel">
        <div v-for="(destination, index) in displayDestinations" :key="destination.id" class="w-[83.7vw] max-w-[320px] flex-none snap-start">
          <DestinationCard variant="small" :destination="destination" :loading="index === 0 ? 'eager' : 'lazy'" :on-click="onDestinationClick" />
        </div>
      </div>
    </div>

    <!-- グリッド ↔ ボタンの縦間隔は 40px (Story セクションと統一) -->
    <div class="mt-10 flex justify-center px-4 md:px-0">
      <!-- Figma: 背景は Background/accent (dark.bg.accent = gold-400 = #C6A86C)、文字色は Foreground/inversed (#1A150E = gray9) — StorySection ボタンと統一 -->
      <button
        class="flex h-12 w-full max-w-[368px] items-center justify-center bg-dark-bg-accent px-6 font-Spectral text-base text-gray9 transition-opacity hover:opacity-90 md:text-[17px]"
        data-testid="view-all-destinations-btn"
        type="button"
        @click="onClickViewAll"
      >
        <span>View all destinations</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import arrowRightGold500 from '@/assets/icon/arrow/right-gold500.svg';
import type { MinimumFeaturedListViewModel, MinimumFeaturedViewModel } from '@/wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured';
import { NAVIGATION_DESTINATIONS_LIMIT } from '@/wabunka-src/constants/navigation';
import DestinationCard from '@/wabunka-src/features/Top/pages/components/DestinationsSection/DestinationCard.vue';

// Z字配置: 1-2=大カード(上段), 3-7=小カード(下段)
const TOP_LARGE_COUNT = 2;

interface Props {
  destinationList: MinimumFeaturedListViewModel;
  onDestinationClick?: (destination: MinimumFeaturedViewModel) => void;
  onClickViewAll?: () => void;
}

const props = defineProps<Props>();

const displayDestinations = computed<MinimumFeaturedListViewModel>(() => props.destinationList.slice(0, NAVIGATION_DESTINATIONS_LIMIT));

const topLargeDestinations = computed<MinimumFeaturedListViewModel>(() => displayDestinations.value.slice(0, TOP_LARGE_COUNT));
const bottomSmallDestinations = computed<MinimumFeaturedListViewModel>(() => displayDestinations.value.slice(TOP_LARGE_COUNT, NAVIGATION_DESTINATIONS_LIMIT));
</script>
